/**
 * The client's copy of the permission bit map.
 *
 * ── Why a copy exists at all ────────────────────────────────────────────────
 *
 * The server enforces. This copy only decides what to RENDER — whether to show a
 * button, whether to put an item in the sidebar. Rendering a control the server
 * will refuse is worse than hiding it: the user clicks, gets a 403, and reads the
 * app as broken rather than as restricted.
 *
 * ── Keeping it honest ───────────────────────────────────────────────────────
 *
 * Two copies of a bit map drift, and a drifted copy grants the wrong thing rather
 * than failing loudly. Two things guard against that:
 *
 *   1. MODULE_IDS are pinned to match `constants/moduleCatalog.js` on the server.
 *      They are how a login's `grants` array maps back to module names.
 *   2. The permission EDITOR never uses this file — it reads
 *      `GET /auth/permission-catalog` so an admin is always editing what the
 *      server actually understands.
 *
 * If you add an action on the server, add it here too. It is append-only there,
 * so it is append-only here.
 */

export const MODULE_IDS = {
  1: "clients",
  2: "users",
  3: "rbac_admin",
  4: "chat",
  5: "projects",
  6: "tasks",
  7: "notifications",
  8: "settings",
  9: "time_tracking",
  10: "billing",
  11: "time_reports",
  12: "drive",
  13: "vault",
}

export const MODULE_KEY_TO_ID = Object.fromEntries(Object.entries(MODULE_IDS).map(([id, key]) => [key, Number(id)]))

export const MODULE_ACTION_BITS = {
  clients: { view: 1, create: 2, update: 4, delete: 8, export: 16, manage_carriers: 32, provision_schema: 64, view_logs: 128 },
  users: { view: 1, create: 2, update: 4, delete: 8, export: 16, assign_role: 32, reset_password: 64, manage_sessions: 128 },
  rbac_admin: { view: 1, create: 2, update: 4, delete: 8, assign_roles: 16, manage_modules: 32 },
  chat: { view: 1, create: 2, update: 4, delete: 8, create_client_thread: 16, manage_participants: 32, attach_records: 64, moderate: 128, export: 256 },
  projects: { view: 1, create: 2, update: 4, delete: 8, export: 16, manage_members: 32, archive: 64, assign_members: 128, manage_billing: 256 },
  tasks: { view: 1, create: 2, update: 4, delete: 8, export: 16, comment: 32, assign: 64, bulk_action: 128, manage_attachments: 256, manage_statuses: 512, view_logs: 1024, manage_assignees: 2048, manage_billing: 4096 },
  notifications: { view: 1, broadcast: 2, manage_templates: 4 },
  settings: { view: 1, create: 2, update: 4, delete: 8, manage_departments: 16, view_logs: 32 },
  time_tracking: { view: 1, log: 2, edit_own: 4, delete_own: 8, export: 16, view_all: 32, edit_any: 64, sign_off: 128, manage_settings: 256 },
  billing: { view: 1, invoice: 2, void: 4, refund: 8, export: 16, manage_settings: 32, schedule: 64, autopay: 128 },
  time_reports: { view: 1, view_all: 2, export: 4 },
  drive: { view: 1, upload: 2, create_folder: 4, rename: 8, move: 16, delete: 32, share: 64, manage_any: 128 },
  vault: { view: 1, create: 2, update: 4, delete: 8, reveal: 16, share: 32, view_audit: 64, manage_any: 128 },
}

export const MODULE_LABELS = {
  clients: "Clients",
  users: "Users",
  rbac_admin: "Permission system",
  chat: "Chat",
  projects: "Projects",
  tasks: "Tasks & Issues",
  notifications: "Notifications",
  settings: "Settings",
  time_tracking: "Time Tracking",
  billing: "Billing & Invoices",
  time_reports: "Time Reports",
  drive: "Files",
  vault: "Password Vault",
}

/*
 * Exposed for the browser check, which compares this copy against
 * GET /auth/permission-catalog.
 *
 * Two copies of a bit map drift, and a drifted copy hides a feature rather than
 * failing loudly — adding a module server-side once left every one of its
 * screens invisible with nothing to say why. The check needs to read this from
 * inside the built bundle, where the module is no longer importable by name.
 */
if (typeof window !== "undefined") {
  window.__TW_PERMISSION_MANIFEST__ = { ids: MODULE_IDS, actions: MODULE_ACTION_BITS }
}
